/* ============================================================
   Velvety client-side currency display.

   Shopify multi-currency needs Shopify Payments, which this
   store does not use (Stripe + Tabby/Tamara), so every market
   is locked to AED. This layer mirrors what the old WordPress
   "Multi Currency Pro" plugin did: it CONVERTS DISPLAYED PRICES
   for reference only. Checkout still charges in AED.

   How it works:
   - Prices are rendered with `data-velvety-money="<AED cents>"`.
   - The shopper picks a currency in the header; choice is saved
     to localStorage.
   - Every [data-velvety-money] element is reformatted from its
     base AED cents using the rates below.
   - A MutationObserver re-converts prices added later (cart
     drawer refresh, quick add, variant switch, etc.).

   Rates are the manual rates carried over from the WordPress
   plugin (per 1 AED). They are static; update here when needed.
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'velvety_currency';
  var RENDERED_ATTR = 'data-velvety-money-rendered';
  var DEFAULT_CODE = 'AED';

  /* code: { name, symbol, rate (per 1 AED), decimals, position } */
  var CURRENCIES = {
    AED: { name: 'UAE Dirham',        symbol: 'AED', rate: 1,      decimals: 2, position: 'left_space' },
    SAR: { name: 'Saudi Riyal',       symbol: 'SAR', rate: 1.02,   decimals: 2, position: 'left_space' },
    QAR: { name: 'Qatari Riyal',      symbol: 'QAR', rate: 0.986,  decimals: 2, position: 'left_space' },
    KWD: { name: 'Kuwaiti Dinar',     symbol: 'KD',  rate: 0.084,  decimals: 3, position: 'left_space' },
    BHD: { name: 'Bahraini Dinar',    symbol: 'BD',  rate: 0.103,  decimals: 3, position: 'left_space' },
    OMR: { name: 'Omani Rial',        symbol: 'OMR', rate: 0.105,  decimals: 3, position: 'left_space' },
    GBP: { name: 'British Pound',     symbol: '£', rate: 0.201, decimals: 2, position: 'left' },
    EUR: { name: 'Euro',              symbol: '€', rate: 0.23,  decimals: 2, position: 'right' },
    IRR: { name: 'Iranian Rial',      symbol: '﷼', rate: 11500, decimals: 0, position: 'right' },
    IQD: { name: 'Iraqi Dinar',       symbol: 'ع.د', rate: 356, decimals: 0, position: 'right' },
    INR: { name: 'Indian Rupee',      symbol: '₹', rate: 24.7,  decimals: 2, position: 'left' },
    ILS: { name: 'Israeli Shekel',    symbol: '₪', rate: 0.844, decimals: 2, position: 'left' },
    CNY: { name: 'Chinese Yuan',      symbol: '¥', rate: 1.88,  decimals: 2, position: 'left' },
    AUD: { name: 'Australian Dollar', symbol: 'A$',  rate: 0.385,  decimals: 2, position: 'left' },
    USD: { name: 'US Dollar',         symbol: '$',   rate: 0.272,  decimals: 2, position: 'left' }
  };
  /* Display/picker order. */
  var ORDER = ['AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'GBP', 'EUR', 'IRR', 'IQD', 'INR', 'ILS', 'CNY', 'AUD', 'USD'];

  var current = DEFAULT_CODE;

  function readStored() {
    try {
      var v = window.localStorage.getItem(STORAGE_KEY);
      if (v && CURRENCIES[v]) return v;
    } catch (e) {}
    return DEFAULT_CODE;
  }
  function writeStored(code) {
    try { window.localStorage.setItem(STORAGE_KEY, code); } catch (e) {}
  }

  function addThousands(intStr) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* cents (AED, integer) -> formatted string in `code`. */
  function format(cents, code) {
    var cur = CURRENCIES[code || current] || CURRENCIES[DEFAULT_CODE];
    var amount = (Number(cents) / 100) * cur.rate;
    if (!isFinite(amount)) amount = 0;
    var fixed = amount.toFixed(cur.decimals);
    var parts = fixed.split('.');
    parts[0] = addThousands(parts[0]);
    var num = parts.join('.');
    switch (cur.position) {
      case 'left': return cur.symbol + num;
      case 'right': return num + cur.symbol;
      case 'right_space': return num + ' ' + cur.symbol;
      default: return cur.symbol + ' ' + num; /* left_space */
    }
  }

  /* Format a single element from its data-velvety-money cents. */
  function formatEl(el) {
    if (!el || !el.hasAttribute('data-velvety-money')) return;
    if (el.getAttribute(RENDERED_ATTR) === current) return;
    var cents = el.getAttribute('data-velvety-money');
    if (cents === '' || cents === null) return;
    var prefix = el.getAttribute('data-velvety-money-prefix') || '';
    var suffix = el.getAttribute('data-velvety-money-suffix') || '';
    el.textContent = prefix + format(cents, current) + suffix;
    el.setAttribute(RENDERED_ATTR, current);
  }

  /* Point an element at a new AED cents value and reformat now.
     Used by JS that rewrites prices (e.g. PDP variant switch). */
  function setEl(el, cents, prefix) {
    if (!el) return;
    el.setAttribute('data-velvety-money', cents);
    if (prefix != null) el.setAttribute('data-velvety-money-prefix', prefix);
    el.removeAttribute(RENDERED_ATTR);
    formatEl(el);
  }

  /* (Re)format every price under `root` (defaults to document). */
  function refresh(root) {
    var scope = root || document;
    if (scope.nodeType === 1 && scope.hasAttribute('data-velvety-money')) formatEl(scope);
    var list = scope.querySelectorAll ? scope.querySelectorAll('[data-velvety-money]') : [];
    for (var i = 0; i < list.length; i++) formatEl(list[i]);
  }

  function setCurrency(code) {
    if (!CURRENCIES[code] || code === current) {
      if (code === current) syncPickers();
      return;
    }
    current = code;
    writeStored(code);
    refresh(document);
    syncPickers();
    document.dispatchEvent(new CustomEvent('velvety:currency-changed', { detail: { code: code } }));
  }

  /* ---------- Picker ---------- */

  function optionMarkup(code) {
    var cur = CURRENCIES[code];
    return (
      '<li role="option" aria-selected="' + (code === current ? 'true' : 'false') + '">' +
        '<button type="button" class="velvety-localization__option' + (code === current ? ' is-selected' : '') + '" ' +
          'data-velvety-currency-option data-code="' + code + '">' +
          '<span class="velvety-localization__option-label">' +
            '<strong>' + code + '</strong> ' +
            '<span class="velvety-localization__option-name">' + cur.name + '</span>' +
          '</span>' +
          (code === current
            ? '<svg class="velvety-localization__option-check" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6l3 3 5-7" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '') +
        '</button>' +
      '</li>'
    );
  }

  function buildOptions(listEl) {
    var html = '';
    for (var i = 0; i < ORDER.length; i++) html += optionMarkup(ORDER[i]);
    html += '<li class="velvety-localization__note" aria-hidden="true">Prices shown for reference. Orders are charged in AED.</li>';
    listEl.innerHTML = html;
  }

  function syncPickers() {
    var roots = document.querySelectorAll('[data-velvety-currency-root]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var label = root.querySelector('[data-velvety-currency-label]');
      if (label) label.textContent = current;
      var listEl = root.querySelector('[data-velvety-currency-options]');
      if (listEl) buildOptions(listEl);
    }
  }

  function bindPicker(root) {
    if (root.__velvetyCurrencyBound) return;
    root.__velvetyCurrencyBound = true;
    var trigger = root.querySelector('[data-velvety-currency-trigger]');
    var options = root.querySelector('[data-velvety-currency-options]');
    if (!trigger || !options) return;

    buildOptions(options);
    var label = root.querySelector('[data-velvety-currency-label]');
    if (label) label.textContent = current;

    function close() {
      options.setAttribute('hidden', '');
      trigger.setAttribute('aria-expanded', 'false');
      root.removeAttribute('data-velvety-open');
    }
    function open() {
      options.removeAttribute('hidden');
      trigger.setAttribute('aria-expanded', 'true');
      root.setAttribute('data-velvety-open', 'true');
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      if (options.hasAttribute('hidden')) open(); else close();
    });
    options.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-velvety-currency-option]');
      if (!btn || !options.contains(btn)) return;
      var code = btn.getAttribute('data-code');
      close();
      if (code) setCurrency(code);
    });
    document.addEventListener('click', function (e) {
      if (options.hasAttribute('hidden')) return;
      if (root.contains(e.target)) return;
      close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !options.hasAttribute('hidden')) { close(); trigger.focus(); }
    });
  }

  function bindAllPickers() {
    var roots = document.querySelectorAll('[data-velvety-currency-root]');
    for (var i = 0; i < roots.length; i++) bindPicker(roots[i]);
  }

  /* ---------- Observe dynamically added prices ---------- */

  function observe() {
    if (!window.MutationObserver) return;
    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue; /* elements only; skip text -> no reformat loop */
          refresh(node);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* Merge admin-editable rates (Theme settings) over the static defaults.
     Output by theme.liquid as window.VELVETY_CURRENCY_RATES = { CODE: "rate" }.
     A blank/invalid field keeps the built-in default. */
  function applyAdminRates() {
    var overrides = window.VELVETY_CURRENCY_RATES;
    if (!overrides) return;
    for (var code in overrides) {
      if (!overrides.hasOwnProperty(code) || !CURRENCIES[code]) continue;
      var r = parseFloat(overrides[code]);
      if (isFinite(r) && r > 0) CURRENCIES[code].rate = r;
    }
  }

  function init() {
    applyAdminRates();
    current = readStored();
    refresh(document);
    bindAllPickers();
    observe();
    window.VelvetyCurrency = {
      format: function (cents) { return format(cents, current); },
      formatEl: formatEl,
      setEl: setEl,
      refresh: refresh,
      setCurrency: setCurrency,
      get: function () { return current; },
      currencies: CURRENCIES
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
